import { Component, inject, input, signal } from '@angular/core';
import { ChatService } from '../../../../services/chat-service/chat.service';
import { Message } from '../../../../services/chat-service/types';
import { Loader } from '../../../../shared/loader';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../../../services/user-service/user.service';

interface FeedbackPanelState {
  type: 'up' | 'down' | 'success' | 'error';
  input: string;
  selectedChips: string[];
}
interface FeedbackData {
  feedback_type: 'up' | 'down';
  feedback_comment: string;
  category: string;
}

@Component({
  selector: 'chat-feedback',
  templateUrl: './chat-feedback.html',
  styleUrl: './chat-feedback.scss',
  imports: [Loader],
})
export class ChatFeedback {
  chatService = inject(ChatService);
  userService = inject(UserService);

  userDetails = this.userService.getUserDetails();

  botResponse = input.required<Message>();
  readonly chatId = input.required<string>();

  isLoading = signal(false);
  readonly responseCopied = signal(false);
  readonly feedbackPanelState = signal<FeedbackPanelState | undefined>(undefined);

  copyResponse = () => {
    navigator.clipboard.writeText(this.botResponse().content).then(() => {
      this.responseCopied.set(true);
      setTimeout(() => this.responseCopied.set(false), 2000);
    });
  };

  handleFeedbackClick = (value: 'up' | 'down') => {
    if (this.feedbackPanelState()?.type === value) {
      this.feedbackPanelState.set(undefined);
      return;
    }
    this.feedbackPanelState.set({
      type: value,
      input: '',
      selectedChips: [],
    });
  };

  closeFeedbackPanel = () => {
    this.feedbackPanelState.set(undefined);
  };

  feedbackChips = (type: FeedbackPanelState['type']): string[] => {
    if (type === 'up') {
      return ['Useful Sources', 'Relevant Response', 'Concise Answer', 'Other Feedback'];
    }
    if (type === 'down') {
      return ['Incorrect Sources', 'Irrelevant Response', 'Unclear Answer', 'Other Feedback'];
    }
    return [];
  };

  selectFeedbackChip = (chip: string) => {
    const panelState = this.feedbackPanelState();
    if (panelState && (panelState.type === 'down' || panelState.type === 'up')) {
      const already = panelState.selectedChips.includes(chip);
      const selectedChips = already
        ? panelState.selectedChips.filter((c) => c !== chip)
        : [...panelState.selectedChips, chip];
      this.feedbackPanelState.set({ ...panelState, selectedChips });
    }
  };

  updatePanelInput = (value: string) => {
    const panelState = this.feedbackPanelState();
    if (panelState) {
      this.feedbackPanelState.set({ ...panelState, input: value });
    }
  };

  submitFeedback = async () => {
    const panelState = this.feedbackPanelState();
    if (panelState && (panelState.type === 'down' || panelState.type === 'up')) {
      const feedbackData: FeedbackData = {
        feedback_type: panelState.type,
        feedback_comment: panelState.input,
        category: panelState.selectedChips.join(','),
      };
      this.isLoading.set(true);
      this.chatService.sendFeedback(this.botResponse().id, feedbackData).subscribe({
        next: () => {
          this.feedbackPanelState.set({ ...panelState, type: 'success' });
          this.generateChatFeedbackMailto(feedbackData);
        },
        error: () => {
          this.feedbackPanelState.set({ ...panelState, type: 'error' });
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
    }
  };

  generateChatFeedbackMailto = ({ feedback_type, category, feedback_comment }: FeedbackData) => {
    const user = this.userDetails.data();
    const name = encodeURIComponent(user?.displayName ?? '');
    const email = encodeURIComponent(user?.userPrincipalName ?? '');
    const link = `mailto:${environment.DATANEX_SUPPORT_EMAIL}?subject=User%20${name}%20%28${email}%29%20submitted%20feedback.&body=Chat%20Id%3A%20${encodeURIComponent(this.chatId())}%0AUp%2FDown%3A%20${encodeURIComponent(feedback_type)}%0ACategory%3A%20${encodeURIComponent(category)}%0A%0AMessage%3A%0A%0A${encodeURIComponent(feedback_comment)}`;

    window.location.href = link;
  };
}
